import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import useSWR from "swr";
import { companyLogoService } from "../../api_client";

const KEY = "my-company-logo";

/** Kept in sync with the backend's CompanyLogoService. */
export const COMPANY_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const COMPANY_LOGO_TYPES = ["image/png", "image/jpeg"] as const;

/**
 * The current user's company logo (shown on their quotation / order PDFs).
 * `logoUrl` is an object URL for previewing the saved image, `null` when no
 * logo is uploaded, and `undefined` while loading.
 */
export function useCompanyLogo() {
  const { data: blob, error, mutate } = useSWR<Blob | null>(
    KEY,
    async () => {
      try {
        const res = await companyLogoService.getMyCompanyLogo({ responseType: "blob" });
        return res.data as unknown as Blob;
      } catch (err) {
        // 404 is the normal "no logo yet" state, not an error.
        if (axios.isAxiosError(err) && err.response?.status === 404) return null;
        throw err;
      }
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  const logoUrl = useMemo(
    () => (blob === undefined ? undefined : blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(() => () => {
    if (logoUrl) URL.revokeObjectURL(logoUrl);
  }, [logoUrl]);

  const [isSaving, setIsSaving] = useState(false);

  const upload = useCallback(
    async (file: File) => {
      setIsSaving(true);
      try {
        await companyLogoService.uploadMyCompanyLogo(file);
        await mutate();
      } finally {
        setIsSaving(false);
      }
    },
    [mutate],
  );

  const remove = useCallback(async () => {
    setIsSaving(true);
    try {
      await companyLogoService.removeMyCompanyLogo();
      await mutate(null, { revalidate: false });
    } finally {
      setIsSaving(false);
    }
  }, [mutate]);

  return { logoUrl, error, isSaving, upload, remove };
}
